import React from 'react';
import { Link } from 'react-router-dom';
import ComplianceCard from '../components/ComplianceCard';
import { CyclePrintView } from '../components/CyclePrintView';
import { IComplianceResult } from '../types';
import { usePageTitle } from '../hooks/usePageTitle';
import SEOHelmet from '../components/SEOHelmet';

// ── Mock data ─────────────────────────────────────────────────────────────────
const daysUntil = (dateStr: string) =>
  Math.max(0, Math.round((new Date(dateStr).getTime() - Date.now()) / 86_400_000));

const exampleInProgress: IComplianceResult = {
  cycleId: 'demo-1', cycleName: '2025–2027 Renewal Cycle',
  professionName: 'Attorney', professionID: 'LAW', stateName: 'New York', stateID: 32,
  totalEarned: 12, totalRequired: 24, percentComplete: 50, isMet: false,
  categories: [
    { categoryTag: 'GENERAL', categoryName: 'General CLE', required: 20, earned: 10, isMet: false },
    { categoryTag: 'ETHICS',  categoryName: 'Ethics',      required: 4,  earned: 2,  isMet: false },
  ],
  warnings: ['12 more credits needed'],
  daysRemaining: daysUntil('2027-04-30'), cycleStartDate: '2025-05-01', cycleEndDate: '2027-04-30',
};

const exampleNearlyComplete: IComplianceResult = {
  cycleId: 'demo-2', cycleName: '2024–2026 Renewal Cycle',
  professionName: 'Architect', professionID: 'ARCH', stateName: 'California', stateID: 5,
  totalEarned: 22, totalRequired: 24, percentComplete: 92, isMet: false,
  categories: [
    { categoryTag: 'GENERAL',        categoryName: 'General',        required: 18, earned: 18, isMet: true  },
    { categoryTag: 'SUSTAINABILITY',  categoryName: 'Sustainability', required: 5,  earned: 4,  isMet: false },
    { categoryTag: 'OTHER',           categoryName: 'Other',          required: 1,  earned: 0,  isMet: false },
  ],
  warnings: ['2 more credits needed in specific categories'],
  daysRemaining: daysUntil('2026-08-31'), cycleStartDate: '2024-09-01', cycleEndDate: '2026-08-31',
};

const exampleCompliant: IComplianceResult = {
  cycleId: 'demo-3', cycleName: '2026 Annual CPE Cycle',
  professionName: 'CPA', professionID: 'CPA', stateName: 'New York', stateID: 32,
  totalEarned: 40, totalRequired: 40, percentComplete: 100, isMet: true,
  categories: [
    { categoryTag: 'GENERAL', categoryName: 'General CPE', required: 28, earned: 28, isMet: true },
    { categoryTag: 'TAX',     categoryName: 'Taxation',    required: 8,  earned: 8,  isMet: true },
    { categoryTag: 'ETHICS',  categoryName: 'Ethics',      required: 4,  earned: 4,  isMet: true },
  ],
  warnings: [], daysRemaining: daysUntil('2026-12-31'), cycleStartDate: '2026-01-01', cycleEndDate: '2026-12-31',
};

const mockPrintClasses = [
  { _id: '1', classname: 'Ethics for CPAs in Public Practice',  classdate: '2026-02-15', classLU: 4.0,  took: true },
  { _id: '2', classname: 'Federal Tax Law Changes 2026',        classdate: '2026-04-10', classLU: 8.0,  took: true },
  { _id: '3', classname: 'Financial Statement Analysis',        classdate: '2026-05-20', classLU: 12.0, took: true },
  { _id: '4', classname: 'Audit Standards & Procedures',        classdate: '2026-06-05', classLU: 8.0,  took: true },
  { _id: '5', classname: 'Advanced Tax Planning Strategies',    classdate: '2026-08-22', classLU: 8.0,  took: true },
];

const mockCategoryCompliance = [
  { categoryTag: 'GENERAL', description: 'General Accounting & Business', minCredits: 28, earned: 28, planned: 0 },
  { categoryTag: 'TAX',     description: 'Taxation',                       minCredits: 8,  earned: 8,  planned: 0 },
  { categoryTag: 'ETHICS',  description: 'Ethics (NY Mandatory)',          minCredits: 4,  earned: 4,  planned: 0 },
];

const featureCards = [
  { icon: 'bi-globe',         color: 'var(--color-accent)',      title: 'Multi-State Support',    body: 'Manage licenses across all 50 states from one unified dashboard. Perfect for professionals with multiple state licenses.' },
  { icon: 'bi-shield-check',  color: 'var(--color-success)',     title: 'Secure Storage',         body: 'All certificates stored with AWS S3 security. Access your documents anytime, even years after your renewal cycle ends.' },
  { icon: 'bi-clock-history', color: 'var(--color-accent-warm)', title: 'Smart Reminders',        body: 'Proactive email alerts about upcoming deadlines and missing credits. Stay ahead instead of scrambling at the last minute.' },
];

// ── Component ─────────────────────────────────────────────────────────────────
const HowItWorks = () => {
  usePageTitle('How It Works');

  return (
    <>
      <SEOHelmet
        title="How It Works - Track CE Credits in 3 Simple Steps"
        description="Learn how CEC Track helps you manage continuing education credits across all states. Quick 3-step setup: add licenses, upload classes, and track compliance automatically."
        keywords="how to track CE credits, continuing education management, license renewal process, CE credit tracker tutorial, multi-state license tracking"
        canonical="https://cectrack.com/how-it-works"
        structuredData={{
          "@context": "https://schema.org", "@type": "HowTo",
          "name": "How to Track Continuing Education Credits with CEC Track",
          "description": "A simple 3-step process to track your professional continuing education credits across multiple states",
          "image": "https://cectrack.com/logo-512.png", "totalTime": "PT10M",
          "estimatedCost": { "@type": "MonetaryAmount", "currency": "USD", "value": "0" },
          "step": [
            { "@type": "HowToStep", "position": 1, "name": "Add License Information", "url": "https://cectrack.com/how-it-works#step1" },
            { "@type": "HowToStep", "position": 2, "name": "Add Your Classes",        "url": "https://cectrack.com/how-it-works#step2" },
            { "@type": "HowToStep", "position": 3, "name": "Assign to Cycles",        "url": "https://cectrack.com/how-it-works#step3" },
          ],
        }}
      />

      {/* ── Hero ── */}
      <section style={{ background: 'var(--color-primary)', padding: '5rem 0 4rem' }}>
        <div className="container" style={{ maxWidth: '1200px', margin: '0 auto', padding: '0 1.5rem' }}>
          <p style={{ fontFamily: 'var(--font-body)', fontSize: 'var(--fs-sm)', fontWeight: 'var(--fw-semibold)', color: 'var(--color-accent)', textTransform: 'uppercase', letterSpacing: '0.12em', marginBottom: '0.5rem' }}>
            Getting Started
          </p>
          <h1 style={{ fontFamily: 'var(--font-display)', fontWeight: 700, fontSize: 'clamp(2.5rem, 5vw, 4.5rem)', color: '#fff', lineHeight: 1.05, marginBottom: '0.75rem', letterSpacing: '-0.01em' }}>
            How CEC Track Works
          </h1>
          <p style={{ fontFamily: 'var(--font-body)', fontSize: 'var(--fs-md)', color: 'rgba(255,255,255,0.65)', maxWidth: '560px', marginBottom: 0 }}>
            Stay compliant with your professional license requirements across multiple states — all from one simple dashboard.
          </p>
        </div>
      </section>

      {/* ── Step 1 ── */}
      <section id="step1" style={{ padding: '7rem 0', background: 'var(--color-surface)' }}>
        <div className="container" style={{ maxWidth: '1200px', margin: '0 auto', padding: '0 1.5rem' }}>
          <StepHeader n="01" color="var(--color-accent)"
            title="Track All Your Licenses in One Dashboard"
            sub="See your compliance status across all states and professions at a glance."
          />
          <div className="row g-4" style={{ marginBottom: '2rem' }}>
            <div className="col-lg-4"><ComplianceCard compliance={exampleInProgress}     showCycleLink={false} /></div>
            <div className="col-lg-4"><ComplianceCard compliance={exampleNearlyComplete} showCycleLink={false} /></div>
            <div className="col-lg-4"><ComplianceCard compliance={exampleCompliant}      showCycleLink={false} /></div>
          </div>
          <Callout icon="bi-info-circle" color="var(--color-accent)">
            <strong>See your progress at a glance:</strong> Track total credits earned, view category breakdowns, monitor deadlines, and know exactly what you need to complete for each license.
          </Callout>
        </div>
      </section>

      {/* ── Step 2 ── */}
      <section id="step2" style={{ padding: '7rem 0', background: 'var(--color-surface-muted)' }}>
        <div className="container" style={{ maxWidth: '1200px', margin: '0 auto', padding: '0 1.5rem' }}>
          <StepHeader n="02" color="var(--color-success)"
            title="Quick 3-Step Setup"
            sub="Add your license information, classes, and assign them to cycles — it's that simple."
          />
          <div className="row align-items-center g-3" style={{ marginBottom: '2rem' }}>
            {[
              { icon: 'bi-patch-check',     title: 'Add License Info',   items: ['State & Profession', 'Cycle Start/End Dates', 'Total Credits Required'],       accent: 'var(--color-primary)' },
              { icon: 'bi-journal-bookmark', title: 'Add Your Classes',   items: ['Course Name', 'Learning Units (LU)', 'Completion Date'],                       accent: 'var(--color-success)' },
              { icon: 'bi-link-45deg',       title: 'Assign to Cycles',   items: ['Match Classes to Cycles', 'Automatic Credit Calculation', 'Category Assignment'], accent: 'var(--color-accent)' },
            ].map((box, i) => (
              <React.Fragment key={box.title}>
                <div className="col-lg-3">
                  <div style={{
                    padding: '2rem 1.5rem', borderRadius: 'var(--radius-md)',
                    border: `2px solid ${box.accent}22`,
                    background: 'var(--color-surface-raised)', boxShadow: 'var(--shadow-card)',
                    minHeight: '220px', display: 'flex', flexDirection: 'column', justifyContent: 'center', textAlign: 'center',
                  }}>
                    <i className={`bi ${box.icon}`} style={{ fontSize: '2.5rem', color: box.accent, marginBottom: '1rem', display: 'block' }} />
                    <h5 style={{ fontFamily: 'var(--font-display)', fontWeight: 700, fontSize: 'var(--fs-lg)', color: 'var(--color-text)', marginBottom: '1rem' }}>{box.title}</h5>
                    <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
                      {box.items.map(item => (
                        <li key={item} style={{ fontFamily: 'var(--font-body)', fontSize: 'var(--fs-sm)', color: 'var(--color-text-muted)', marginBottom: '0.4rem' }}>
                          <span style={{ color: box.accent, marginRight: '0.4rem' }}>✓</span>{item}
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
                {i < 2 && (
                  <div className="col-lg-1 text-center d-none d-lg-block">
                    <i className="bi bi-arrow-right" style={{ fontSize: '1.5rem', color: 'var(--color-text-muted)' }} />
                  </div>
                )}
              </React.Fragment>
            ))}
          </div>
          <Callout icon="bi-check-circle" color="var(--color-success)">
            <strong>Simple and intuitive:</strong> Set up your licenses in minutes. Our system automatically tracks your progress and calculates credits for each category.
          </Callout>
        </div>
      </section>

      {/* ── Step 3 ── */}
      <section id="step3" style={{ padding: '7rem 0', background: 'var(--color-surface)' }}>
        <div className="container" style={{ maxWidth: '1200px', margin: '0 auto', padding: '0 1.5rem' }}>
          <StepHeader n="03" color="var(--color-accent-warm)"
            title="Generate Detailed Compliance Reports"
            sub="Get comprehensive reports ready for license renewal submissions, anytime."
          />
          <div style={{ border: '1px solid var(--color-border)', borderRadius: 'var(--radius-md)', overflow: 'hidden', boxShadow: 'var(--shadow-lg)', marginBottom: '2rem' }}>
            <CyclePrintView
              cycleName="2026 CPA Annual CPE"
              state="NY" period="January 1, 2026 – December 31, 2026"
              creditsRequired={40} totalLUCompleted={40} totalLUPlanned={0} luToBeCompleted={0}
              attachedClasses={mockPrintClasses}
              firstName="John" lastName="Doe"
              professionName="Certified Public Accountant" licenseNumber="CPA-12345678"
              creditTerm="CPE Hours" categoryCompliance={mockCategoryCompliance}
            />
          </div>
          <Callout icon="bi-file-earmark-text" color="var(--color-accent-warm)">
            <strong>Professional reports ready to print:</strong> Generate comprehensive reports anytime for license renewals, audits, or your own records. All certificates are securely stored and linked to each course.
          </Callout>
        </div>
      </section>

      {/* ── Features ── */}
      <section style={{ padding: '7rem 0', background: 'var(--color-primary)' }}>
        <div className="container" style={{ maxWidth: '1200px', margin: '0 auto', padding: '0 1.5rem' }}>
          <div style={{ textAlign: 'center', marginBottom: '4rem' }}>
            <h2 style={{ fontFamily: 'var(--font-display)', fontWeight: 700, fontSize: 'clamp(2rem, 4vw, 3rem)', color: '#ffffff', marginBottom: '0.75rem', lineHeight: 1.1 }}>
              Why Professionals Choose CEC Track
            </h2>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '1.25rem' }}>
            {featureCards.map(({ icon, color, title, body }) => (
              <div key={title}
                style={{ padding: '2rem', background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.08)', borderRadius: 'var(--radius-md)', transition: 'background var(--transition-base), transform var(--transition-base)', cursor: 'default' }}
                onMouseEnter={e => { e.currentTarget.style.background = 'rgba(42,191,191,0.08)'; e.currentTarget.style.transform = 'translateY(-4px)'; }}
                onMouseLeave={e => { e.currentTarget.style.background = 'rgba(255,255,255,0.05)'; e.currentTarget.style.transform = 'translateY(0)'; }}
              >
                <i className={`bi ${icon}`} style={{ fontSize: '1.75rem', color, marginBottom: '1.25rem', display: 'block' }} />
                <h3 style={{ fontFamily: 'var(--font-display)', fontWeight: 600, fontSize: 'var(--fs-lg)', color: '#ffffff', marginBottom: '0.75rem' }}>{title}</h3>
                <p style={{ fontFamily: 'var(--font-body)', fontSize: 'var(--fs-sm)', color: 'rgba(255,255,255,0.6)', lineHeight: 1.7, margin: 0 }}>{body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── CTA ── */}
      <section style={{ padding: '7rem 0', background: 'var(--color-surface-muted)' }}>
        <div className="container" style={{ maxWidth: '760px', margin: '0 auto', padding: '0 1.5rem', textAlign: 'center' }}>
          <h2 style={{ fontFamily: 'var(--font-display)', fontWeight: 700, fontSize: 'clamp(2rem, 4vw, 3.25rem)', color: 'var(--color-text)', marginBottom: '1rem', lineHeight: 1.1, letterSpacing: '-0.01em' }}>
            Ready to Simplify Your<br />
            <em style={{ color: 'var(--color-accent)', fontStyle: 'italic' }}>License Management?</em>
          </h2>
          <p style={{ fontFamily: 'var(--font-body)', fontSize: 'var(--fs-md)', color: 'var(--color-text-muted)', marginBottom: '2.5rem', lineHeight: 1.7 }}>
            Join professionals across healthcare, engineering, legal, finance, and design industries.
          </p>
          <div style={{ display: 'flex', gap: '1rem', justifyContent: 'center', flexWrap: 'wrap', marginBottom: '1.5rem' }}>
            <Link to="/register"
              style={{ display: 'inline-flex', alignItems: 'center', gap: '0.5rem', padding: '0.875rem 2rem', background: 'var(--color-primary)', color: '#fff', fontFamily: 'var(--font-body)', fontWeight: 'var(--fw-semibold)', fontSize: 'var(--fs-base)', borderRadius: 'var(--radius-md)', textDecoration: 'none', transition: 'background var(--transition-base), transform var(--transition-base)' }}
              onMouseEnter={e => { e.currentTarget.style.background = 'var(--color-primary-hover)'; e.currentTarget.style.transform = 'translateY(-2px)'; }}
              onMouseLeave={e => { e.currentTarget.style.background = 'var(--color-primary)'; e.currentTarget.style.transform = 'translateY(0)'; }}
            >
              Get Started Free <i className="bi bi-arrow-right" />
            </Link>
            <Link to="/login"
              style={{ display: 'inline-flex', alignItems: 'center', gap: '0.5rem', padding: '0.875rem 2rem', background: 'transparent', color: 'var(--color-text)', fontFamily: 'var(--font-body)', fontWeight: 'var(--fw-medium)', fontSize: 'var(--fs-base)', borderRadius: 'var(--radius-md)', textDecoration: 'none', border: '1px solid var(--color-border-strong)', transition: 'border-color var(--transition-base)' }}
              onMouseEnter={e => { e.currentTarget.style.borderColor = 'var(--color-text)'; }}
              onMouseLeave={e => { e.currentTarget.style.borderColor = 'var(--color-border-strong)'; }}
            >
              Sign In
            </Link>
          </div>
          <p style={{ fontFamily: 'var(--font-body)', fontSize: 'var(--fs-xs)', color: 'var(--color-text-muted)' }}>
            <i className="bi bi-check-circle me-1" style={{ color: 'var(--color-success)' }} />
            Free to start · No credit card required
          </p>
        </div>
      </section>
    </>
  );
};

// ── Sub-components ─────────────────────────────────────────────────────────────
const StepHeader = ({ n, color, title, sub }: { n: string; color: string; title: string; sub: string }) => (
  <div style={{ display: 'flex', gap: '2rem', alignItems: 'flex-start', marginBottom: '3rem' }}>
    <div style={{ flexShrink: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '0.6rem' }}>
      <div style={{ width: '100px', height: '100px', borderRadius: '50%', background: color, display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: `0 6px 24px ${color}55` }}>
        <span style={{ fontFamily: 'var(--font-display)', fontWeight: 700, fontSize: '2.75rem', color: '#fff', lineHeight: 1 }}>{n}</span>
      </div>
      <span style={{ fontFamily: 'var(--font-mono)', fontSize: 'var(--fs-sm)', fontWeight: 'var(--fw-bold)', color, textTransform: 'uppercase', letterSpacing: '0.14em', whiteSpace: 'nowrap' }}>
        Step {n}
      </span>
    </div>
    <div style={{ paddingTop: '0.25rem' }}>
      <h2 style={{ fontFamily: 'var(--font-display)', fontWeight: 700, fontSize: 'clamp(1.75rem, 3.5vw, 2.75rem)', color: 'var(--color-text)', marginBottom: '0.75rem', lineHeight: 1.15, letterSpacing: '-0.01em' }}>
        {title}
      </h2>
      <p style={{ fontFamily: 'var(--font-body)', fontSize: 'var(--fs-md)', color: 'var(--color-text-muted)', maxWidth: '600px', margin: 0, lineHeight: 1.6 }}>
        {sub}
      </p>
    </div>
  </div>
);

const Callout = ({ icon, color, children }: { icon: string; color: string; children: React.ReactNode }) => (
  <div style={{ padding: '1rem 1.25rem', background: `color-mix(in srgb, ${color} 8%, var(--color-surface-raised))`, border: `1px solid color-mix(in srgb, ${color} 30%, transparent)`, borderLeft: `4px solid ${color}`, borderRadius: 'var(--radius-md)', display: 'flex', alignItems: 'flex-start', gap: '0.75rem' }}>
    <i className={`bi ${icon}`} style={{ color, fontSize: '1.1rem', marginTop: '0.1rem', flexShrink: 0 }} />
    <span style={{ fontFamily: 'var(--font-body)', fontSize: 'var(--fs-sm)', color: 'var(--color-text)', lineHeight: 1.65 }}>{children}</span>
  </div>
);

export default HowItWorks;
